import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Icon, IconsNgModule } from '@seech/icons-ng';

let nextId = 0;

/** Visual weight of the header chip. `danger` is for destructive intent only. */
export type ModalShellTone = 'primary' | 'danger' | 'success' | 'warning';

/**
 * Shared chrome for a form modal.
 *
 * Every subsite modal previously repeated the same header/body/actions markup
 * behind its own namespaced classes (`.mch-form-modal__*`, `.aff-form-modal__*`).
 * Eleven copies meant eleven places to fix a header, and they had already
 * drifted apart in spacing. This owns that chrome once; a feature modal supplies
 * only its own fields.
 *
 * Two behavioural fixes come with it, both of which the per-app copies got
 * wrong: the header and the action bar stay put while a long form scrolls
 * (previously the whole dialog scrolled, so on the invitation editor the title
 * and the Save button both scrolled out of reach), and the body is the only
 * scroll container.
 *
 * Theming goes through `--seech-primary-rgb`, which every subsite's theme layer
 * already sets, so this renders in the host app's brand colour without the
 * library knowing anything about `--mch-*` or `--aff-*`.
 */
@Component({
  standalone: true,
  selector: 'sub-modal-shell',
  imports: [IconsNgModule],
  templateUrl: './modal-shell.component.html',
  styleUrls: ['./modal-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalShellComponent {
  /** Sentence-case, states what the dialog does. Required. */
  @Input({ required: true }) title = '';

  /**
   * One line of context under the title. Optional, but a form that needs a
   * caveat should carry it here rather than in a field's helper text.
   */
  @Input() hint = '';

  /**
   * Short uppercase context label above the title — the record or area being
   * acted on, so the title itself does not have to repeat it.
   */
  @Input() eyebrow = '';

  /** Leading chip glyph. Bound as a directive input, never as an attribute. */
  @Input() icon: Icon = 'edit';

  @Input() tone: ModalShellTone = 'primary';

  /**
   * Hides the close control while a submit is in flight, so a half-saved form
   * cannot be dismissed mid-request.
   */
  @Input() busy = false;

  @Output() dismissed = new EventEmitter<void>();

  /** Ties `aria-labelledby` to the rendered title without the caller wiring it. */
  readonly titleId = `modal-shell-title-${nextId++}`;

  close(): void {
    if (this.busy) return;
    this.dismissed.emit();
  }
}
